import { useTheme } from "@mui/material";
import React from "react";

import { serializeTimestamp, Timestamp as TimestampType } from "@/shared/text";

import { useWorkspaceTimer } from "../visualization/WorkspaceCtx";

import { HoverActionToolbar, hoverActions } from "./HoverActionToolbar";
import { pickDisplayLabelNode } from "./pickDisplayLabel";
import { useJumpToTimestampAction, useSetAnchorAction } from "./timeFormat";

/**
 * Clickable wall-clock timestamp, used for explicit `[label](timestamp:<ns>)`
 * markdown links and the legacy `formatTimestampToMarkdownString` helper.
 *
 * The display text comes from {@link pickDisplayLabelNode}: a user-authored
 * custom label (e.g. `[start of segment](timestamp:…)`) is preserved
 * verbatim; any numeric-looking label is treated as the AI's attempt at
 * rendering the timestamp itself and overridden with the formatter.
 *
 * Naming an instant also earns the hover toolbar's actions: "Jump to
 * timestamp" while a visualizer is open to move, and "Set as t=0" wherever a
 * {@link TimestampFormatProvider} is in reach to hold the anchor.
 */
export const Timestamp: React.FC<{
  children: React.ReactNode;
  timestamp: TimestampType;
}> = ({ children, timestamp }) => {
  const theme = useTheme();
  const timer = useWorkspaceTimer();
  const jumpAction = useJumpToTimestampAction(timestamp);
  const setAnchorAction = useSetAnchorAction(timestamp);

  return (
    <HoverActionToolbar actions={hoverActions(jumpAction, setAnchorAction)}>
      <a
        href={serializeTimestamp(timestamp)}
        style={{
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.text.secondary,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          timer.seekTo(timestamp);
        }}
      >
        {pickDisplayLabelNode(children, timestamp)}
      </a>
    </HoverActionToolbar>
  );
};
